import './styles/main.css';
import { StrictMode, Component } from 'react';
import { createRoot } from 'react-dom/client';
import { __ } from '@wordpress/i18n';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PitchField from '@/components/PitchField/index.jsx';

// ─── Error Boundary ───────────────────────────────────────────────────────────
// Catches any runtime exception in the PitchField tree so a single broken field
// doesn't crash the whole page.

class ErrorBoundary extends Component {
	constructor( props ) {
		super( props );
		this.state = { hasError: false };
	}
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	componentDidCatch( error, info ) {
		console.error( 'wsp: PitchField error', error, info );
	}
	render() {
		if ( this.state.hasError ) {
			return (
				<p className="wsp-pitch-field__error" role="alert">
					{__( 'An unexpected error occurred. Please reload the page.', 'web-sponsors-pitch' )}
				</p>
			);
		}
		return this.props.children;
	}
}

// Shared QueryClient across all mounts on the page
const queryClient = new QueryClient( {
	defaultOptions: {
		queries: {
			retry: 1,
		},
	},
} );

// Track mounted roots to prevent double-mounting
const mounted = new WeakMap();

/**
 * Mounts a PitchField (or DevWrapper in dev mode) into a container inserted before an input element.
 *
 * Required on input element:
 *   data-field-type   - Field type slug matching a folder under plugin/fields/ (e.g. "normal")
 *
 * Optional data attributes on input:
 *   data-mode         - "interactive" (default) | "preview"
 *
 * @param {HTMLElement} inputEl - The .wsp-tiles-input element
 */
function mountPitchField( inputEl ) {
	if ( mounted.has( inputEl ) ) return;

	const fieldType = inputEl.dataset.fieldType;
	const mode      = inputEl.dataset.mode ?? 'interactive';

	if ( ! fieldType ) {
		console.warn( 'wsp: missing data-field-type on input element', inputEl );
		return;
	}

	// Create a container div and insert it before the input element
	const container = document.createElement( 'div' );
	container.className = 'wsp-pitch-field-container';
	inputEl.parentNode.insertBefore( container, inputEl );

	const root = createRoot( container );

	root.render(
		<StrictMode>
			<ErrorBoundary>
				<QueryClientProvider client={queryClient}>
					<PitchField fieldType={fieldType} mode={mode}>
						<PitchField.Grid />
						<PitchField.PriceDisplay />
						<PitchField.Legend />
					</PitchField>
				</QueryClientProvider>
			</ErrorBoundary>
		</StrictMode>
	);

	mounted.set( inputEl, root );
}

/**
 * Scans a subtree for .wsp-tiles-input elements and mounts PitchField before each one.
 *
 * @param {ParentNode} [parent]
 */
function mountAll( parent = document ) {
	parent.querySelectorAll( '.wsp-tiles-input' ).forEach( mountPitchField );
}

// Initial mount
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', () => mountAll() );
} else {
	mountAll();
}

// Watch for dynamically inserted input elements (Gravity Forms AJAX, etc.)
const observer = new MutationObserver( ( mutations ) => {
	for ( const mutation of mutations ) {
		for ( const node of mutation.addedNodes ) {
			if ( ! ( node instanceof Element ) ) continue;
			if ( node.matches( '.wsp-tiles-input' ) ) mountPitchField( node );
			mountAll( node );
		}
	}
} );

observer.observe( document.documentElement, { childList: true, subtree: true } );
